import { observer as observerLite } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import {
    COMPOSE_ONLY_ATTACHMENT_CLASS_NAME,
    LinkIdContainer,
    OWA_INSERT_LINK_PLACEHOLDER_CLASSNAME,
} from 'owa-attachment-link-common';
import { CLICK_EVENT_NAME, CONTEXT_MENU_EVENT_NAME } from 'owa-compose-link';
import { ControlIcons } from 'owa-control-icons';
import { lazyOperateContent, OperateContentType } from 'owa-editor';
import EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { ATTRIBUTE_CONTENTEDITABLE_NAME } from 'owa-link-core';
import { RecipientContainer } from 'owa-link-data';
import * as trace from 'owa-trace';
import React, { useEffect } from 'react';
import * as ReactDOM from 'react-dom';
import { PositionType } from 'roosterjs-editor-types';
import getInsertLinkViewState from '../selectors/getInsertLinkViewState';
import InsertLinkViewState from '../store/schema/InsertLinkViewState';
import { showLinkPlaceholderContextMenu } from './LinkPlaceholderContextMenu';

interface LinkPlaceholderProps {
    editorViewState: EditorViewState;
    linkId: string;
    operateContent: OperateContentType;
    getRecipientWells: () => RecipientContainer[];
    linkIdContainer: LinkIdContainer;
    fromAddress: string;
    originalPlaceHolder: HTMLElement;
    targetWindow: Window;
    sxsId: string;
    shouldDisplayInNextLine: boolean;
}

const linkStyles = require('./LinkPlaceholder.scss');
const classNames = require('classnames/bind').bind(linkStyles);

const LinkPlaceholder = observerLite(function ObserverLiteLinkPlaceholder(
    linkPlaceholderProps: LinkPlaceholderProps
) {
    const linkPlaceholderRef: React.RefObject<HTMLDivElement> = React.createRef();

    // After we successfully retrieve sharing link, we will replace this with beautiful link and remove the view state.
    const viewState: InsertLinkViewState | undefined = getInsertLinkViewState(
        linkPlaceholderProps.linkId
    );

    useEffect(() => {
        // Mount event
        let linkPlaceholder: HTMLSpanElement | null = linkPlaceholderRef.current;

        const contextMenuHandler = (event: MouseEvent) => {
            if (linkPlaceholder && !viewState?.isRetrievingSharingLink) {
                showLinkPlaceholderContextMenu(
                    linkPlaceholder,
                    linkPlaceholderProps.linkId,
                    linkPlaceholderProps.editorViewState,
                    linkPlaceholderProps.getRecipientWells,
                    linkPlaceholderProps.linkIdContainer,
                    linkPlaceholderProps.fromAddress,
                    linkPlaceholderProps.targetWindow,
                    linkPlaceholderProps.sxsId
                );
            }
            event.preventDefault();
        };

        if (linkPlaceholder) {
            linkPlaceholder.setAttribute(ATTRIBUTE_CONTENTEDITABLE_NAME, 'false');
            linkPlaceholder.addEventListener(CLICK_EVENT_NAME, contextMenuHandler);
            linkPlaceholder.addEventListener(CONTEXT_MENU_EVENT_NAME, contextMenuHandler);

            linkPlaceholderProps.operateContent(
                linkPlaceholderProps.editorViewState,
                (editor, range, domUtils) => {
                    editor.focus();

                    if (linkPlaceholderProps.originalPlaceHolder) {
                        editor.replaceNode(
                            linkPlaceholderProps.originalPlaceHolder,
                            linkPlaceholder
                        );
                    } else if (viewState?.displayText) {
                        const selectionNode = editor.getSelectionRange();
                        if (
                            selectionNode &&
                            selectionNode.startContainer === selectionNode.endContainer &&
                            selectionNode.startContainer.textContent.substring(
                                selectionNode.startOffset,
                                selectionNode.endOffset
                            ) === viewState?.displayText
                        ) {
                            const textContent: string = selectionNode.startContainer.textContent;

                            if (selectionNode.startOffset > 0) {
                                const leftText: string = textContent.substring(
                                    0,
                                    selectionNode.startOffset
                                );
                                const leftTextNode = document.createTextNode(leftText);
                                selectionNode.startContainer.parentNode.insertBefore(
                                    leftTextNode,
                                    selectionNode.startContainer
                                );
                            }
                            if (selectionNode.endOffset < textContent.length) {
                                const rightText: string = textContent.substring(
                                    selectionNode.endOffset,
                                    textContent.length
                                );
                                const rightTextNode = document.createTextNode(rightText);
                                selectionNode.startContainer.parentNode.insertBefore(
                                    rightTextNode,
                                    selectionNode.startContainer.nextSibling
                                );
                            }
                            editor.replaceNode(selectionNode.startContainer, linkPlaceholder);
                        } else {
                            trace.errorThatWillCauseAlert(
                                `Did not find the selection Text: ${viewState?.displayText}`
                            );
                        }
                    } else {
                        if (linkPlaceholderProps.shouldDisplayInNextLine) {
                            linkPlaceholder = domUtils.wrap(linkPlaceholder);
                        }
                        editor.insertNode(linkPlaceholder);
                    }
                    return linkPlaceholderProps.originalPlaceHolder
                        ? null
                        : domUtils.createRange(linkPlaceholder, PositionType.After);
                }
            );
        }

        return () => {
            // UnMount event
            if (linkPlaceholder) {
                linkPlaceholder.removeEventListener(CLICK_EVENT_NAME, contextMenuHandler);
                linkPlaceholder.removeEventListener(CONTEXT_MENU_EVENT_NAME, contextMenuHandler);
            }
        };
    }, []);

    const conditionalStyles = {
        textUnderline: viewState?.isRetrievingSharingLink,
        failureColor: !viewState?.isRetrievingSharingLink,
    };

    const inlineStyle: React.CSSProperties = {
        backgroundColor: 'rgb(244,244,244)',
        display: 'inline-block',
    };

    return (
        <div
            style={inlineStyle}
            ref={linkPlaceholderRef}
            id={linkPlaceholderProps.linkId}
            className={classNames(
                OWA_INSERT_LINK_PLACEHOLDER_CLASSNAME,
                linkStyles.placeholder,
                conditionalStyles
            )}>
            {viewState && (
                <React.Fragment>
                    <span className={COMPOSE_ONLY_ATTACHMENT_CLASS_NAME}>
                        <div
                            className={
                                viewState?.isRetrievingSharingLink
                                    ? linkStyles.linkSpinner
                                    : linkStyles.hidden
                            }
                        />
                        <Icon
                            iconName={ControlIcons.RemoveLink}
                            className={
                                viewState?.isRetrievingSharingLink
                                    ? linkStyles.hidden
                                    : linkStyles.failureIcon
                            }
                        />
                        {viewState?.displayText ? viewState?.displayText : viewState?.file.name}
                    </span>
                </React.Fragment>
            )}
        </div>
    );
});

export async function createLinkPlaceholder(
    editorViewState: EditorViewState,
    linkId: string,
    getRecipientWells: () => RecipientContainer[],
    linkIdContainer: LinkIdContainer,
    fromAddress: string,
    originalPlaceHolder: HTMLElement,
    targetWindow: Window,
    sxsId: string,
    shouldDisplayInNextLine: boolean
) {
    const document = (targetWindow || window).document;
    const anchorContainer = document.createElement('span');
    const operateContent = await lazyOperateContent.import();
    ReactDOM.render(
        <React.StrictMode>
            <LinkPlaceholder
                editorViewState={editorViewState}
                linkId={linkId}
                operateContent={operateContent}
                getRecipientWells={getRecipientWells}
                linkIdContainer={linkIdContainer}
                fromAddress={fromAddress}
                originalPlaceHolder={originalPlaceHolder}
                targetWindow={targetWindow}
                sxsId={sxsId}
                shouldDisplayInNextLine={shouldDisplayInNextLine}
            />
        </React.StrictMode>,
        anchorContainer
    );
}
